/**
 * Which "an agency is configured here but switched off" reports have already been sent this session,
 * keyed `departmentId:agency`.
 *
 * A module of its own, with NO imports, on purpose. The state belongs to `useAgencyAvailability`, but
 * the reset belongs to the logout path in `contexts/AuthProvider` — and importing the hook from there
 * would close a real runtime cycle (`AuthProvider` -> `useAgencyAvailability` -> `HospitalDataProvider`
 * -> `AuthProvider`, which imports `useAuth` at `HospitalDataProvider.tsx:64`). A leaf module both
 * sides import instead has no cycle to resolve.
 *
 * Deduplication is the point: three surfaces call `useAgencyAvailability` (`CreateShiftContent`,
 * `PostToAgencyDialog`, `useCanPostRequestToAgency`) and several can be mounted at once, so without
 * this one deactivated integration reports two or three times — measuring how many agency surfaces the
 * admin happened to have open rather than how often posting was actually suppressed.
 */
const reportedSuppressions = new Set<string>();

/** True the FIRST time a given department+agency suppression is seen; false every time after. */
export const claimAgencySuppressionReport = (departmentId: string, agency: string): boolean => {
	const key = `${departmentId}:${agency}`;
	if (reportedSuppressions.has(key)) return false;
	reportedSuppressions.add(key);
	return true;
};

/**
 * Drops the bookkeeping. Called from logout beside `resetAnalyticsSession()`, for the same reason that
 * exists: a different user signing in on the same tab must start with a fresh event identity rather
 * than inheriting this one's "already reported" state and silently under-counting their first look at
 * a deactivated integration.
 */
export const resetAgencySuppressionReports = (): void => {
	reportedSuppressions.clear();
};
